import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import {
  Trophy, Warning, Lightning, Target, ArrowRight,
  WhatsappLogo, EnvelopeSimple, ChartBar, Crosshair,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import apiClient from '@/lib/apiClient';

const SectionEyebrow = ({ children }) => (
  <div className="text-xs tracking-[0.2em] uppercase font-bold text-teal-700 mb-1">
    {children}
  </div>
);

// ---------- Executive Alert (CEO Briefing) ----------
const Row = ({ label, color, children }) => (
  <tr className="border-b border-slate-100 last:border-b-0">
    <td className="py-2 pr-3 align-top text-[10px] font-bold uppercase tracking-wider whitespace-nowrap"
        style={{ color }}>{label}</td>
    <td className="py-2 text-sm text-slate-800 leading-relaxed">{children}</td>
  </tr>
);

const ExecutiveAlertCard = ({ alert }) => {
  if (!alert) return null;
  const colour = alert.maturity_colour || '#0D9488';
  const risk = alert.most_significant_risk;
  const opp = alert.most_significant_opportunity;
  const impact = alert.estimated_business_impact;
  return (
    <Card
      className="p-6 mb-6 shadow-md border-2"
      style={{ borderColor: colour }}
      data-testid="exec-alert-card"
    >
      <div className="flex items-baseline justify-between flex-wrap gap-3 pb-3 border-b-2"
           style={{ borderColor: colour }}>
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold" style={{ color: colour }}>
            Section 1
          </div>
          <h2 className="text-2xl font-black text-slate-900">Executive Summary</h2>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase font-semibold text-slate-500 tracking-widest">Overall AWSAI Score</div>
          <div className="text-3xl font-black leading-none" style={{ color: colour }}>
            {alert.current_awsai_score.toFixed(2)}
            <span className="text-sm text-slate-500 font-semibold"> / 5.00</span>
          </div>
        </div>
      </div>
      <table className="w-full mt-2">
        <tbody>
          <Row label="Maturity Level" color="#475569">
            <strong className="text-slate-900">{alert.maturity_level}</strong>
          </Row>
          <Row label="Benchmark Position" color="#475569">{alert.benchmark_position}</Row>
          <Row label="Main Operational Risk" color="#B91C1C">
            <strong className="text-red-800">{risk.domain} ({risk.score.toFixed(1)} / 5.0)</strong>
            <span className="text-slate-700"> — {risk.description}</span>
          </Row>
          <Row label="Main Transformation Opportunity" color="#15803D">
            <strong className="text-green-800">{opp.workflow_name}</strong>
            <span className="text-slate-600"> ({opp.classification})</span>
            <span className="text-slate-700"> — {opp.opportunity_text}</span>
          </Row>
          <Row label="Estimated Business Impact" color="#475569">
            <ul className="space-y-2">
              <li><strong>Productivity:</strong> {impact.productivity}</li>
              <li><strong>Time saved:</strong> {impact.time_saved}</li>
              <li><strong>Error reduction:</strong> {impact.error_reduction}</li>
              <li><strong>Process stability:</strong> {impact.process_stability}</li>
              <li><strong>AI readiness:</strong> {impact.ai_readiness}</li>
            </ul>
          </Row>
          <Row label="Priority Management Action" color={colour}>
            {alert.priority_management_action}
          </Row>
          <Row label="Recommended Next Step" color="#0F766E">
            {alert.expected_benefit} A focused consultation is recommended to validate these findings and develop a detailed implementation roadmap.
          </Row>
        </tbody>
      </table>
      <div className="mt-4 pt-3 border-t border-dashed border-slate-300 text-xs text-slate-500 italic text-center">
        This Executive Summary provides a high-level overview of the assessment. Detailed analysis and recommended actions follow below.
      </div>
    </Card>
  );
};

// ---------- Maturity Card ----------
const MaturityLevelCard = ({ maturity, overallScore }) => {
  const [lo, hi] = maturity.range;
  return (
    <Card className="p-6 mb-6 border-l-[6px] shadow-sm" style={{ borderLeftColor: maturity.colour }}
          data-testid="pro-maturity-card">
      <SectionEyebrow>Section 4 · Interpretation</SectionEyebrow>
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="text-xl font-bold text-slate-900">
          Level {maturity.level}: {maturity.name}
        </h3>
        <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white"
              style={{ background: maturity.colour }}>
          {lo.toFixed(2)} – {hi.toFixed(2)}
        </span>
        <span className="text-sm text-slate-500 ml-auto">
          Your score: <strong className="text-slate-900">{overallScore.toFixed(2)} / 5.00</strong>
        </span>
      </div>
      <div className="mt-4 space-y-3">
        <p className="text-sm text-slate-700 leading-relaxed">
          <strong className="text-slate-900">What this means: </strong>{maturity.meaning}
        </p>
        <p className="text-sm text-slate-700 leading-relaxed">
          <strong className="text-slate-900">Urgency: </strong>{maturity.urgency}
        </p>
      </div>
    </Card>
  );
};

// ---------- Benchmark Card ----------
const BenchmarkCard = ({ benchmark, overallScore }) => (
  <Card className="p-6 mb-6 shadow-sm" data-testid="pro-benchmark-card">
    <SectionEyebrow>Section 5 · Benchmark Analysis</SectionEyebrow>
    <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
      <ChartBar weight="fill" className="text-teal-600" size={22} />
      Where you stand
    </h3>
    <div className="bg-gradient-to-br from-teal-50 to-slate-50 border border-teal-100 rounded-lg p-5">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-teal-700">Your Position</div>
      <div className="text-2xl font-black text-teal-900 mt-1">{benchmark.group_name}</div>
      <div className="text-sm text-teal-800 font-medium mt-1">{benchmark.position}</div>
      <div className="grid grid-cols-3 gap-4 mt-4">
        <div>
          <div className="text-[10px] uppercase text-slate-500 font-semibold">Score</div>
          <div className="text-lg font-bold text-slate-900">{overallScore.toFixed(2)} / 5</div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-slate-500 font-semibold">Percentile</div>
          <div className="text-lg font-bold text-slate-900">{benchmark.percentile}<span className="text-xs text-slate-500">th</span></div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-slate-500 font-semibold">Gap to Mature</div>
          <div className="text-lg font-bold text-slate-900">{benchmark.gap_to_mature.toFixed(2)}</div>
        </div>
      </div>
    </div>
    <h4 className="text-sm font-bold text-slate-900 mt-5 mb-2">Typical characteristics of your group</h4>
    <ul className="text-sm text-slate-700 space-y-1 list-disc ml-5">
      {benchmark.characteristics.map((c) => <li key={c}>{c}</li>)}
    </ul>
    {benchmark.competitor_intelligence && (
      <div className="mt-5 p-4 bg-slate-50 border-l-4 border-teal-500 rounded">
        <h4 className="text-sm font-bold text-teal-800 mb-1">What competitors are doing differently</h4>
        <p className="text-sm text-slate-700 leading-relaxed">{benchmark.competitor_intelligence}</p>
      </div>
    )}
    {benchmark.act_now_rationale && (
      <div className="mt-3 p-4 bg-amber-50 border-l-4 border-amber-500 rounded">
        <h4 className="text-sm font-bold text-amber-900 mb-1">Why act now</h4>
        <p className="text-sm text-amber-900/90 leading-relaxed">{benchmark.act_now_rationale}</p>
      </div>
    )}
  </Card>
);

// ---------- Competitive Position Card ----------
const CompetitiveCard = ({ position }) => (
  <Card className="p-6 mb-6 shadow-sm" data-testid="pro-competitive-card">
    <SectionEyebrow>Section 4 · Benchmark Positioning</SectionEyebrow>
    <h3 className="text-xl font-bold text-slate-900 mb-2 flex items-center gap-2">
      <Crosshair weight="fill" className="text-teal-600" size={22} />
      Benchmark Position and Comparative Assessment
    </h3>
    <div className="inline-block bg-slate-900 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
      {position.stance}
    </div>
    <p className="text-sm text-slate-700 leading-relaxed">{position.narrative}</p>
    <div className="grid md:grid-cols-2 gap-4 mt-5">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="text-xs uppercase font-bold text-green-800 mb-2 flex items-center gap-1">
          <Trophy size={14} weight="fill" /> Relative Areas of Strength
        </div>
        <ul className="text-sm space-y-1">
          {position.strengths.map((s) => (
            <li key={s.domain} className="flex justify-between">
              <span className="text-slate-700">{s.domain}</span>
              <strong className="text-green-800">{s.score.toFixed(1)}</strong>
            </li>
          ))}
        </ul>
      </div>
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="text-xs uppercase font-bold text-red-800 mb-2 flex items-center gap-1">
          <Warning size={14} weight="fill" /> Areas Requiring Attention
        </div>
        <ul className="text-sm space-y-1">
          {position.vulnerabilities.map((v) => (
            <li key={v.domain} className="flex justify-between">
              <span className="text-slate-700">{v.domain}</span>
              <strong className="text-red-800">{v.score.toFixed(1)}</strong>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </Card>
);

// ---------- Priority Heatmap ----------
const priorityStyle = (p) => ({
  'High':                  'bg-red-100   text-red-800',
  'Medium':                'bg-amber-100 text-amber-800',
  'Low':                   'bg-blue-100  text-blue-800',
  'Maintain / Leverage':   'bg-green-100 text-green-800',
}[p] || 'bg-slate-100 text-slate-700');

const PriorityHeatmapCard = ({ rows }) => (
  <Card className="p-6 mb-6 shadow-sm" data-testid="pro-heatmap-card">
    <SectionEyebrow>Section 6</SectionEyebrow>
    <h3 className="text-xl font-bold text-slate-900 mb-3 flex items-center gap-2">
      <Target weight="fill" className="text-teal-600" size={22} />
      Priority Heat Map
    </h3>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b border-slate-200">
            <th className="py-2 font-semibold text-slate-600">Domain</th>
            <th className="py-2 font-semibold text-slate-600 text-right">Your Score</th>
            <th className="py-2 font-semibold text-slate-600">Benchmark Position</th>
            <th className="py-2 font-semibold text-slate-600 text-right">Gap to Leader</th>
            <th className="py-2 font-semibold text-slate-600">Priority</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.domain} className="border-b border-slate-100">
              <td className="py-2 text-slate-900 font-medium">{r.domain}</td>
              <td className="py-2 text-right font-bold text-slate-900">{r.score.toFixed(1)}</td>
              <td className="py-2 text-slate-700">{r.position}</td>
              <td className="py-2 text-right text-slate-700">{r.gap_to_leader.toFixed(1)}</td>
              <td className="py-2">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${priorityStyle(r.priority)}`}>
                  {r.priority}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Card>
);

// ---------- Urgency Narrative ----------
const UrgencyCard = ({ text }) => (
  <Card className="p-6 mb-6 shadow-sm bg-amber-50 border-amber-200 border-l-[6px] border-l-amber-500"
        data-testid="pro-urgency-card">
    <SectionEyebrow>Section 6b</SectionEyebrow>
    <h3 className="text-xl font-bold text-amber-900 mb-2 flex items-center gap-2">
      <Lightning weight="fill" className="text-amber-600" size={22} />
      Why This Requires Attention Now
    </h3>
    <p className="text-sm text-amber-900/90 leading-relaxed">{text}</p>
  </Card>
);

// ---------- Roadmap ----------
const RoadmapCard = ({ phases }) => (
  <Card className="p-6 mb-6 shadow-sm" data-testid="pro-roadmap-card">
    <SectionEyebrow>Section 10</SectionEyebrow>
    <h3 className="text-xl font-bold text-slate-900 mb-3">SMART-Workflow Transformation Roadmap</h3>
    <div className="space-y-4">
      {phases.map((p) => (
        <div key={p.phase} className="border-l-4 border-teal-500 pl-4 py-2">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h4 className="font-bold text-slate-900">{p.phase}</h4>
            <span className="text-xs text-slate-500">{p.window}</span>
          </div>
          <p className="text-sm text-slate-700 mt-1"><strong>Objective:</strong> {p.objective}</p>
          <ul className="text-sm text-slate-600 mt-1 list-disc ml-5">
            {p.actions.map((a) => <li key={a}>{a}</li>)}
          </ul>
          <p className="text-sm text-teal-700 mt-1"><strong>Outcome:</strong> {p.outcome}</p>
          <p className="text-sm text-amber-700 mt-1"><strong>Management decision required:</strong> {p.management_decision}</p>
        </div>
      ))}
    </div>
  </Card>
);

// ---------- Consultation CTA ----------
const ConsultationCta = ({ cta }) => (
  <Card className="p-6 mb-6 shadow-md bg-gradient-to-br from-slate-900 to-teal-900 text-white"
        data-testid="pro-consultation-cta">
    <SectionEyebrow>
      <span className="text-teal-300">Section 13</span>
    </SectionEyebrow>
    <h3 className="text-2xl font-bold mb-2">{cta.title}</h3>
    <p className="text-slate-200 leading-relaxed mb-4">{cta.body}</p>
    <div className="flex gap-3 flex-wrap">
      <a
        href={cta.whatsapp_url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-400 text-white font-bold px-4 py-2 rounded-lg transition"
        data-testid="cta-whatsapp-button"
      >
        <WhatsappLogo size={18} weight="fill" />
        {cta.whatsapp_label}
      </a>
      <a
        href={`mailto:${cta.email}?subject=AWSAI%20Consultation%20Request`}
        className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-slate-100 font-bold px-4 py-2 rounded-lg transition"
        data-testid="cta-email-button"
      >
        <EnvelopeSimple size={18} weight="fill" />
        {cta.email_label}
      </a>
    </div>
    <p className="text-xs text-slate-300 mt-3">
      {cta.phone} · {cta.email}
    </p>
  </Card>
);

// ---------- Domain Deep Insights ----------
const DomainDeepInsightsCard = ({ insights }) => {
  if (!insights?.length) return null;
  return (
    <Card className="p-6 mb-6 shadow-sm" data-testid="pro-domain-insights-card">
      <SectionEyebrow>Section 4b · Per-Domain Diagnostic</SectionEyebrow>
      <h3 className="text-xl font-bold text-slate-900 mb-3">AWSAI Domain Deep Dive</h3>
      <p className="text-sm text-slate-600 mb-4">
        For each domain: a diagnostic headline that uses your score relative to your other domains, then a Why-This-Matters / If-Nothing-Changes / Business-Benefit / Management-Response narrative.
      </p>
      <div className="space-y-4">
        {insights.map((d) => (
          <div key={d.domain} className="border border-slate-200 rounded-lg p-4">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <h4 className="font-bold text-slate-900">{d.domain}</h4>
              <span className="text-sm text-slate-600">{d.score.toFixed(1)} / 5.0 · {d.benchmark_line}</span>
            </div>
            <p className="italic text-xs text-slate-600 mt-2 leading-relaxed">{d.headline}</p>
            <div className="mt-3 space-y-3 text-sm leading-relaxed">
              <p><strong className="text-slate-900">Interpretation. </strong><span className="text-slate-700">{d.interpretation || d.why_matters}</span></p>
              <p><strong className="text-red-800">Management Implication. </strong><span className="text-slate-700">{d.management_implication || d.if_nothing_changes}</span></p>
              <p><strong className="text-teal-800">Recommended Action. </strong><span className="text-slate-700">{d.recommended_action || d.management_response}</span></p>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ---------- Per-Workflow AI matrix (unique reasoning) ----------
const WorkflowInsightCard = ({ analyses }) => {
  if (!analyses?.length) return null;
  return (
    <Card className="p-6 mb-6 shadow-sm" data-testid="pro-workflows-card">
      <SectionEyebrow>Section 7</SectionEyebrow>
      <h3 className="text-xl font-bold text-slate-900 mb-3">AI Opportunity Matrix</h3>
      <p className="text-sm text-slate-600 mb-4">Each workflow you submitted gets a unique classification, reason, and first-action step.</p>
      <div className="space-y-3">
        {analyses.map((a) => (
          <div key={a.name} className="border-l-[5px] pl-4 py-2 rounded-r"
               style={{ borderLeftColor: a.colour }}>
            <div className="flex items-baseline gap-3 flex-wrap">
              <h4 className="font-bold text-slate-900">{a.name}</h4>
              <span className="text-xs font-bold uppercase tracking-wide" style={{ color: a.colour }}>
                {a.classification}
              </span>
            </div>
            <p className="text-sm text-slate-700 mt-1"><strong>Reason:</strong> {a.reason}</p>
            <p className="text-sm text-slate-700"><strong>AI potential:</strong> {a.ai_potential}</p>
            <p className="text-sm text-slate-700"><strong>Recommended technology:</strong> {a.recommended_tech}</p>
            <p className="text-sm text-amber-800"><strong>Workflow-specific risk:</strong> {a.automation_risk}</p>
            <p className="text-sm text-green-800"><strong>Opportunity:</strong> {a.opportunity}</p>
            <p className="text-sm text-teal-700 flex items-start gap-1 mt-1">
              <ArrowRight size={14} weight="bold" className="mt-0.5 flex-shrink-0" />
              <span><strong>First action:</strong> {a.first_action_step}</span>
            </p>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ---------- Disclaimer ----------
const Disclaimer = ({ text }) => (
  <div className="mt-6 px-4 py-3 bg-slate-50 border border-dashed border-slate-300 rounded text-xs text-slate-600 leading-relaxed"
       data-testid="pro-disclaimer">
    <strong className="uppercase tracking-wider text-slate-500 text-[10px]">Disclaimer · </strong>
    {text}
  </div>
);

// ===== Main entry point =====

// Module-level fetch helper. Lives outside the hook so the `useEffect` body
// has the absolute minimum surface area. All effects (state setters) are
// passed in — never closed over. Returns nothing.
const loadProInsights = async (assessmentId, signal, onData, onLoading) => {
  try {
    const response = await apiClient.get(
      `/reports/${assessmentId}/pro-insights`,
      { signal },
    );
    if (!signal.aborted) onData(response.data);
  } catch (err) {
    if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
    toast.error('Failed to load Professional insights');
  } finally {
    if (!signal.aborted) onLoading(false);
  }
};

// Custom hook: fetches Professional insights for a given assessmentId,
// returns { insights, loading }. The `AbortController` is unavoidably effect-
// local because aborting a previous in-flight request on assessmentId change
// is part of the contract. Static analysers cannot list effect-local values
// or globals (`AbortController`) as deps; the only reactive value is
// `assessmentId`. The setState setters are stable per React docs.
const useProInsights = (assessmentId) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const controller = new AbortController();
    loadProInsights(assessmentId, controller.signal, setInsights, setLoading);
    return () => controller.abort();
  }, [assessmentId]);

  return { insights, loading };
};

const LoadingSkeleton = () => (
  <Card className="p-6 mb-6 animate-pulse" data-testid="pro-insights-loading">
    <div className="h-6 bg-slate-200 rounded w-2/3 mb-3"></div>
    <div className="h-4 bg-slate-200 rounded w-full mb-2"></div>
    <div className="h-4 bg-slate-200 rounded w-5/6"></div>
  </Card>
);

const ProfessionalInsights = ({ assessmentId, overallScore }) => {
  const { insights, loading } = useProInsights(assessmentId);

  if (loading) return <LoadingSkeleton />;
  if (!insights) return null;

  return (
    <div data-testid="professional-insights-section">
      <ExecutiveAlertCard alert={insights.executive_alert} />
      <MaturityLevelCard maturity={insights.maturity} overallScore={overallScore} />
      <BenchmarkCard benchmark={insights.benchmark} overallScore={overallScore} />
      <CompetitiveCard position={insights.competitive_position} />
      <DomainDeepInsightsCard insights={insights.domain_deep_insights} />
      <PriorityHeatmapCard rows={insights.priority_heatmap} />
      <UrgencyCard text={insights.urgency_narrative} />
      <WorkflowInsightCard analyses={insights.workflow_analyses} />
      <RoadmapCard phases={insights.roadmap} />
      <ConsultationCta cta={insights.consultation_cta} />
      <Disclaimer text={insights.disclaimer} />
    </div>
  );
};

export default ProfessionalInsights;
